/**
 * Products Management
 * CRUD with search, category filtering, active toggle.
 * Redesigned with teal brand aesthetic and refined visual hierarchy.
 *
 * Field name mapping:
 *   Backend GET returns raw snake_case DB columns: product_id, product_code, product_name, product_category, product_description, unit_of_measure, active_status, is_standard
 *   Backend POST expects snake_case: product_code, product_name, product_category, product_description, unit_of_measure, is_standard
 *   Backend PUT expects snake_case: product_code, product_name, product_category, product_description, unit_of_measure, active_status, is_standard
 *
 * TypeScript since ST-00227 (was index.js). Audit batch ORD:
 *   ORD-10 Unit of Measure offers only what the products CHECK constraint accepts, and a
 *          failed save shows the server's reason in the form.
 *   ORD-12 Add / Edit / Delete / the active toggle only for roles that may manage orders.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Search,
  X,
  Check,
  Loader,
  Edit2,
  Trash2,
  ArrowLeft,
  Filter,
  AlertCircle,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import { TableSkeleton, EmptyState, InlineAlert } from '../shared';
import ThemedSelect from '../../../common/ThemedSelect';
import { useAuth } from '../../../../types/commonComponents';
import {
  apiErrorMessage,
  DEFAULT_PRODUCT_UNIT,
  ORDERS_MANAGE,
  PRODUCT_UNITS,
} from '../shared/orderHelpers';
import type { ProductPayload, ProductRow } from '../../../../types/clientPages/orderManagement';

const CATEGORIES = ['Raw Material', 'Finished Goods', 'Components', 'Packaging', 'Other'];

const FONT_BODY = 'DM Sans, sans-serif';
const FONT_MONO = 'IBM Plex Mono, monospace';
const LABEL = 'block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5';

type ProductFormErrors = Partial<Record<keyof ProductPayload, string>>;

const EMPTY_FORM: ProductPayload = {
  product_code: '',
  product_name: '',
  product_category: '',
  product_description: '',
  unit_of_measure: DEFAULT_PRODUCT_UNIT,
  active_status: true,
  is_standard: false,
};

/** Map a product row to the form fields for editing. */
const dbToForm = (dbProduct: ProductRow): ProductPayload => ({
  product_code: dbProduct.product_code || '',
  product_name: dbProduct.product_name || '',
  product_category: dbProduct.product_category || '',
  product_description: dbProduct.product_description || '',
  // A legacy unit the database no longer accepts is offered as the default instead.
  unit_of_measure:
    dbProduct.unit_of_measure && PRODUCT_UNITS.includes(dbProduct.unit_of_measure)
      ? dbProduct.unit_of_measure
      : DEFAULT_PRODUCT_UNIT,
  active_status: dbProduct.active_status != null ? dbProduct.active_status : true,
  is_standard: dbProduct.is_standard != null ? dbProduct.is_standard : false,
});

/* ─── Product Modal ───────────────────────── */
interface ProductModalProps {
  product: ProductRow | null;
  onSave: () => void;
  onCancel: () => void;
}

const ProductModal = ({ product, onSave, onCancel }: ProductModalProps) => {
  const [form, setForm] = useState<ProductPayload>(product ? dbToForm(product) : EMPTY_FORM);
  const [errors, setErrors] = useState<ProductFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof ProductPayload>(field: K, value: ProductPayload[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    const e: ProductFormErrors = {};
    if (!form.product_code) e.product_code = 'Required';
    if (!form.product_name) e.product_name = 'Required';
    if (!form.product_category) e.product_category = 'Required';
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaveError(null);
    try {
      setSaving(true);
      if (product) {
        await orderService.updateProduct(product.product_id, form);
      } else {
        await orderService.createProduct(form);
      }
      onSave();
    } catch (err) {
      console.error('Failed to save product:', err);
      setSaveError(apiErrorMessage(err, 'Failed to save product'));
    } finally {
      setSaving(false);
    }
  };

  const title = product ? 'Edit Product' : 'New Product';
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-modal-title"
        className="bg-surface rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center">
              {product ? (
                <Edit2 className="w-4 h-4 text-white" />
              ) : (
                <Plus className="w-4 h-4 text-white" />
              )}
            </div>
            <h3
              id="product-modal-title"
              className="text-lg font-bold text-slate-900"
              style={{ fontFamily: FONT_BODY }}
            >
              {title}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Product Code */}
          <div>
            <label htmlFor="product-code" className={LABEL}>
              Product Code <span className="text-red-400">*</span>
            </label>
            <input
              id="product-code"
              type="text"
              value={form.product_code}
              onChange={(e) => set('product_code', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors ${errors.product_code ? 'border-red-400' : 'border-slate-200'}`}
              placeholder="e.g., PROD-001"
              style={{ fontFamily: FONT_MONO }}
            />
            {errors.product_code && (
              <p className="text-red-500 text-xs mt-1 font-medium">{errors.product_code}</p>
            )}
          </div>

          {/* Product Name */}
          <div>
            <label htmlFor="product-name" className={LABEL}>
              Product Name <span className="text-red-400">*</span>
            </label>
            <input
              id="product-name"
              type="text"
              value={form.product_name}
              onChange={(e) => set('product_name', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors ${errors.product_name ? 'border-red-400' : 'border-slate-200'}`}
              placeholder="e.g., Premium Steel Rods"
            />
            {errors.product_name && (
              <p className="text-red-500 text-xs mt-1 font-medium">{errors.product_name}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <span className={LABEL}>
              Category <span className="text-red-400">*</span>
            </span>
            <ThemedSelect
              value={form.product_category}
              onChange={(e) => set('product_category', String(e.target.value))}
              aria-label="Category"
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm bg-surface transition-colors ${errors.product_category ? 'border-red-400' : 'border-slate-200'}`}
            >
              <option value="">Select Category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </ThemedSelect>
            {errors.product_category && (
              <p className="text-red-500 text-xs mt-1 font-medium">{errors.product_category}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="product-description" className={LABEL}>
              Description
            </label>
            <textarea
              id="product-description"
              value={form.product_description}
              onChange={(e) => set('product_description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors"
              placeholder="Optional product description..."
            />
          </div>

          {/* Unit of Measure — the values ck_products_unit_of_measure accepts (ORD-10) */}
          <div>
            <span className={LABEL}>Unit of Measure</span>
            <ThemedSelect
              value={form.unit_of_measure}
              onChange={(e) => set('unit_of_measure', String(e.target.value))}
              aria-label="Unit of Measure"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm bg-surface transition-colors"
            >
              {PRODUCT_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </ThemedSelect>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              role="switch"
              aria-checked={form.active_status}
              aria-label="Active Product"
              onClick={() => set('active_status', !form.active_status)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${form.active_status ? 'bg-emerald-500' : 'bg-slate-300'}`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-surface shadow transition-transform duration-200 ${form.active_status ? 'translate-x-[22px]' : 'translate-x-[3px]'}`}
              />
            </button>
            <span className="text-sm font-medium text-slate-700">Active Product</span>
          </div>

          {saveError && <InlineAlert>{saveError}</InlineAlert>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-slate-100">
          <button
            onClick={onCancel}
            className="px-4 py-2.5 text-slate-600 font-semibold text-sm rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40 transition-colors text-sm font-semibold flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {product ? 'Update' : 'Create'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Confirm Dialog ──────────────────────── */
const ConfirmDialog = ({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-surface rounded-xl shadow-2xl max-w-md w-full p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-amber-500" />
        </div>
        <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: FONT_BODY }}>
          Confirm Action
        </h3>
      </div>
      <p className="text-sm text-slate-600 mb-5 leading-relaxed">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2.5 text-slate-600 font-semibold text-sm rounded-lg hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold"
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════ */
const Products = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(ORDERS_MANAGE);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ProductRow | null>(null);
  const [deleting, setDeleting] = useState<ProductRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getProducts();
      setProducts(data.products || []);
    } catch (e) {
      console.error('Failed to fetch products:', e);
      setActionError(apiErrorMessage(e, 'Failed to load the products'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const q = search.toLowerCase();
  const filtered = products.filter(
    (p) =>
      (!q ||
        (p.product_code || '').toLowerCase().includes(q) ||
        (p.product_name || '').toLowerCase().includes(q)) &&
      (!catFilter || p.product_category === catFilter)
  );

  const categories = [
    ...new Set(products.map((p) => p.product_category).filter((c): c is string => !!c)),
  ];

  const handleToggleActive = async (product: ProductRow) => {
    setActionError(null);
    try {
      const formData = dbToForm(product);
      formData.active_status = !product.active_status;
      await orderService.updateProduct(product.product_id, formData);
      fetchProducts();
    } catch (err) {
      console.error('Failed to toggle product status:', err);
      setActionError(apiErrorMessage(err, 'Failed to change the product status'));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setActionError(null);
    try {
      await orderService.deleteProduct(deleting.product_id);
      fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
      setActionError(apiErrorMessage(err, 'Failed to deactivate the product'));
    } finally {
      setDeleting(null);
    }
  };

  const openNew = () => {
    setEditing(null);
    setShowModal(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" style={{ fontFamily: FONT_BODY }}>
      {/* Back Navigation */}
      <button
        onClick={() => navigate('/settings/modules/order-management/master-data')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600 font-medium mb-5 transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        Master Data
      </button>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-teal-600 flex items-center justify-center shadow-sm">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: FONT_BODY }}>
                Products
              </h1>
              {!loading && (
                <span
                  className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-teal-50 text-teal-700 border border-teal-200"
                  style={{ fontFamily: FONT_MONO }}
                >
                  {filtered.length}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Manage your product catalog and categories
            </p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={openNew}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 text-sm font-semibold shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        )}
      </div>

      {actionError && (
        <InlineAlert className="mb-4" onDismiss={() => setActionError(null)}>
          {actionError}
        </InlineAlert>
      )}

      {/* Filter Bar */}
      <div className="bg-surface rounded-xl border border-slate-200 p-4 mb-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or name..."
              aria-label="Search products"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <ThemedSelect
              value={catFilter}
              onChange={(e) => setCatFilter(String(e.target.value))}
              aria-label="Category filter"
              className="pl-9 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm bg-surface appearance-none cursor-pointer min-w-[180px] transition-colors"
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </ThemedSelect>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No products found"
            description={
              search || catFilter
                ? 'Try adjusting your filters'
                : canManage
                  ? 'Add your first product to get started'
                  : 'No products have been set up yet'
            }
            action={canManage && !search && !catFilter ? 'Add Product' : undefined}
            onAction={canManage && !search && !catFilter ? openNew : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  {['Code', 'Name', 'Category', 'Unit', 'Status'].map((h) => (
                    <th
                      key={h}
                      className="px-5 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-widest"
                    >
                      {h}
                    </th>
                  ))}
                  {canManage && (
                    <th className="px-5 py-3.5 text-right text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p, i) => (
                  <tr
                    key={p.product_id}
                    className={`hover:bg-slate-50 transition-colors animate-slideUp ${!p.active_status ? 'opacity-50' : ''}`}
                    style={{ animationDelay: `${i * 30}ms` }}
                  >
                    {/* Code */}
                    <td className="px-5 py-3.5">
                      <span
                        className="text-sm font-bold text-slate-900"
                        style={{ fontFamily: FONT_MONO }}
                      >
                        {p.product_code}
                      </span>
                    </td>

                    {/* Name + Description */}
                    <td className="px-5 py-3.5">
                      <div className="text-sm text-slate-800 font-medium">{p.product_name}</div>
                      {p.product_description && (
                        <div className="text-xs text-slate-400 truncate max-w-xs mt-0.5">
                          {p.product_description}
                        </div>
                      )}
                    </td>

                    {/* Category Badge */}
                    <td className="px-5 py-3.5">
                      {p.product_category && (
                        <span className="inline-flex items-center px-2.5 py-0.5 text-[11px] font-semibold bg-teal-50 text-teal-700 rounded-full border border-teal-200">
                          {p.product_category}
                        </span>
                      )}
                    </td>

                    {/* Unit */}
                    <td className="px-5 py-3.5">
                      <span className="text-xs text-slate-600">{p.unit_of_measure}</span>
                    </td>

                    {/* Active Toggle (read-only without orders.manage) */}
                    <td className="px-5 py-3.5">
                      {canManage ? (
                        <button
                          onClick={() => handleToggleActive(p)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${p.active_status ? 'bg-emerald-500' : 'bg-slate-300'}`}
                          title={
                            p.active_status
                              ? 'Active - click to deactivate'
                              : 'Inactive - click to activate'
                          }
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 rounded-full bg-surface shadow-sm transition-transform duration-200 ${p.active_status ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
                          />
                        </button>
                      ) : (
                        <span className="text-xs text-slate-500">
                          {p.active_status ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    {canManage && (
                      <td className="px-5 py-3.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditing(p);
                              setShowModal(true);
                            }}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                            title="Edit product"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleting(p)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete product"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <ProductModal
          product={editing}
          onSave={() => {
            setShowModal(false);
            setEditing(null);
            fetchProducts();
          }}
          onCancel={() => {
            setShowModal(false);
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          message={`Deactivate "${deleting.product_name}"? It stays in this list as inactive and can be switched back on; new orders can no longer use it.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
};

export default Products;
